/**
 * Quick add — the "+" control on product grid cards.
 *
 * Click "+"      -> reveals the product's sizes inline over the image
 * Click a size   -> adds that variant to the cart and opens the cart drawer
 *
 * Submitting goes through theme.AjaxProduct so this behaves exactly like every
 * other add-to-cart in the theme: it POSTs to routes.cartAdd and dispatches
 * `ajaxProduct:added` from the form, which CartDrawer listens for to rebuild
 * and open the drawer. Listeners are delegated on document so grid items
 * injected later (collection filtering, infinite scroll, recommendations)
 * work without re-initialising anything.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'quick-add--open';
  var ADDING_CLASS = 'quick-add__size--adding';

  function closeAll(except) {
    document.querySelectorAll('.' + OPEN_CLASS).forEach(function (el) {
      if (el === except) return;
      el.classList.remove(OPEN_CLASS);
      var toggle = el.querySelector('.quick-add__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  // theme.AjaxProduct binds a submit handler to the form. Attach it once, lazily,
  // so we don't walk every card on every page load.
  function ensureAjax(form) {
    if (form.dataset.quickAddReady === 'true') return true;
    if (!window.theme || typeof theme.AjaxProduct !== 'function') return false;
    new theme.AjaxProduct(form);
    form.dataset.quickAddReady = 'true';
    return true;
  }

  function submitForm(form) {
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      // Safari < 16 has no requestSubmit; dispatching submit still triggers the handler.
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;

    // "+" on a single-variant product: submit straight away, no size row.
    var direct = target.closest('.quick-add__toggle--direct');
    if (direct) {
      var directForm = direct.closest('.quick-add__form');
      if (directForm && ensureAjax(directForm)) {
        evt.preventDefault();
        submitForm(directForm);
      }
      // If theme.js hasn't loaded yet, fall through to a normal form POST.
      return;
    }

    // "+" on a product with sizes: toggle the size row.
    var toggle = target.closest('.quick-add__toggle');
    if (toggle && !toggle.classList.contains('quick-add__toggle--link')) {
      evt.preventDefault();
      var wrap = toggle.closest('.quick-add');
      if (!wrap) return;
      var isOpen = wrap.classList.contains(OPEN_CLASS);
      closeAll();
      if (!isOpen) {
        wrap.classList.add(OPEN_CLASS);
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // A size was chosen: set the variant id and hand off to theme.AjaxProduct.
    var size = target.closest('.quick-add__size');
    if (size) {
      evt.preventDefault();
      if (size.disabled) return;

      var form = size.closest('.quick-add__form');
      if (!form) return;

      var idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      idInput.value = size.getAttribute('data-variant-id');

      if (!ensureAjax(form)) return;

      size.classList.add(ADDING_CLASS);
      submitForm(form);
      return;
    }

    // Any click outside an open quick-add closes it.
    if (!target.closest('.quick-add')) closeAll();
  });

  // Once the item lands in the cart, collapse the row and clear the pending state.
  document.addEventListener('ajaxProduct:added', function () {
    document.querySelectorAll('.' + ADDING_CLASS).forEach(function (el) {
      el.classList.remove(ADDING_CLASS);
    });
    closeAll();
  });

  document.addEventListener('ajaxProduct:error', function () {
    document.querySelectorAll('.' + ADDING_CLASS).forEach(function (el) {
      el.classList.remove(ADDING_CLASS);
    });
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape' || evt.key === 'Esc') closeAll();
  });

  /* --------------------------------------------------------------------------
   * Cards in the recently-viewed slider are built by theme.js rather than from
   * snippets/product-grid-item.liquid, so they arrive without a quick add.
   * Build the same markup for them here from the product's JSON.
   * ------------------------------------------------------------------------ */

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  var PLUS_ICON = '<svg class="quick-add__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>';

  function buildQuickAdd(product) {
    if (!product || !product.available) return '';

    var variants = product.variants || [];
    var optionCount = (product.options || []).length;
    var label = escapeHtml(product.title);

    var formOpen = '<div class="quick-add"><form class="quick-add__form" action="/cart/add" method="post" enctype="multipart/form-data">' +
      '<input type="hidden" name="quantity" value="1">';

    // Single variant: the + adds straight away.
    if (variants.length === 1) {
      return formOpen +
        '<input type="hidden" name="id" value="' + variants[0].id + '">' +
        '<button type="submit" class="quick-add__toggle quick-add__toggle--direct add-to-cart" aria-label="Add ' + label + ' to cart">' +
        PLUS_ICON + '</button></form></div>';
    }

    // One option (Size): the + reveals the sizes.
    if (optionCount === 1) {
      var sizes = variants.map(function (v) {
        return '<button type="button" class="quick-add__size' + (v.available ? '' : ' quick-add__size--sold-out') +
          '" data-variant-id="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + '</button>';
      }).join('');

      return formOpen +
        '<input type="hidden" name="id" value="">' +
        '<button type="button" class="quick-add__toggle" aria-expanded="false" aria-label="Add ' + label + ' to cart">' +
        PLUS_ICON + '</button>' +
        '<div class="quick-add__sizes">' + sizes + '</div>' +
        '<button type="submit" class="add-to-cart quick-add__submit visually-hidden" tabindex="-1" aria-hidden="true">Add</button>' +
        '</form></div>';
    }

    // More than one option can't be expressed in a single row — link out.
    return '<div class="quick-add"><a href="' + escapeHtml(product.url) + '" class="quick-add__toggle quick-add__toggle--link" aria-label="View ' + label + '">' +
      PLUS_ICON + '</a></div>';
  }

  function fetchProduct(handle, attempt) {
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        /* The theme renders several of these at once; a single failed request
           would otherwise leave that card permanently without a +. */
        if ((attempt || 0) < 1) return fetchProduct(handle, (attempt || 0) + 1);
        throw err;
      });
  }

  function hydrateCard(card) {
    if (card.dataset.quickAddHydrated === 'true') return;

    var link = card.querySelector('a[href*="/products/"]');
    var mask = card.querySelector('.grid-product__image-mask');
    if (!mask || !link || mask.querySelector('.quick-add')) return;

    var href = link.getAttribute('href');
    var match = href.match(/\/products\/([^/?#]+)/);
    if (!match) return;

    /* On these cards the theme nests the image inside the product link. A form
       and buttons inside an <a> is invalid markup and the submit never fires, so
       lift the image out of the link first and restore click-through ourselves. */
    if (link.contains(mask)) {
      var content = card.querySelector('.grid-product__content') || card;
      mask.dataset.quickAddHref = href;
      content.insertBefore(mask, link);
    }

    fetchProduct(match[1])
      .then(function (product) {
        var html = buildQuickAdd(product);
        if (html) mask.insertAdjacentHTML('beforeend', html);
        card.dataset.quickAddHydrated = 'true';
      })
      .catch(function () {
        /* Left unmarked so a later pass can retry. */
      });
  }

  /* The image is no longer wrapped in the product link, so send clicks on it to
     the product — unless they landed on the quick add. */
  document.addEventListener('click', function (evt) {
    var t = evt.target;
    if (!t || typeof t.closest !== 'function') return;
    if (t.closest('.quick-add')) return;

    var mask = t.closest('.grid-product__image-mask[data-quick-add-href]');
    if (mask) window.location.href = mask.dataset.quickAddHref;
  });

  function hydrateTracks() {
    document.querySelectorAll('[data-slider-track] .grid-product').forEach(hydrateCard);
  }

  /* Cards whose lookup failed stay unmarked, so sweep again shortly after. */
  function hydrateTracksWithRetry() {
    hydrateTracks();
    setTimeout(hydrateTracks, 1200);
    setTimeout(hydrateTracks, 4000);
  }

  function watchTracks() {
    document.querySelectorAll('[data-slider-track]').forEach(function (track) {
      if (track.dataset.quickAddWatched === 'true') return;
      track.dataset.quickAddWatched = 'true';
      if (window.MutationObserver) {
        new MutationObserver(hydrateTracksWithRetry).observe(track, { childList: true });
      }
    });
    hydrateTracksWithRetry();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchTracks);
  } else {
    watchTracks();
  }

  document.addEventListener('shopify:section:load', watchTracks);
})();
